import React, { useState } from 'react';

import { theme } from '~/theme';

import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Stack, Typography } from '@mui/material';
import { GridColDef, GridColumnVisibilityModel, GridExpandMoreIcon } from '@mui/x-data-grid';

import CopyableField from '~/components/Common/CopyableField';
import PageSubheader from '~/components/Common/Elements/PageSubheader';
import Spinner from '~/components/Common/Spinner';
import DebugModal from '~/components/DebugModal/DebugModal';
import PromoItemField from '~/components/PromoV3/PromoItemField';
import { PromoRequestDeviceType } from '~/components/PromoV3/PromoRequestDeviceType';
import { PromoRequestSourceFormatter } from '~/components/PromoV3/PromoRequestSource';
import Table from '~/components/v2/common/Table/Table';

import {
  PromoMeta,
  PromoRequestFilters,
  PromoRequestSource,
  getPromoProductDisplayName,
} from '~/services/PromoService';

import currencyFormatter from '~/utils/currencyFormatter';
import DateFormatterDetailed from '~/utils/dateFormatterDetails';
import statusCodeFormatter from '~/utils/statusCodeFormatter';

import TravellersList from '../PromoPage/TravellersList';
import ColumnSelector from '../PromoTester/ColumnSelector';
import PromoCodeUser from '../formatters/PromoCodeUser';
import { PromoRequestItem, VoucherApplied } from '../hooks/usePromoRequests';

import { getRequestKey } from './PromoRequests';

interface PromoRequestItemsProps {
  meta: PromoMeta;
  promoRequestItems?: Array<PromoRequestItem>;
  highlightedRows: Array<string>;
  isLoading: boolean;
  onRowClick?: (params: PromoRequestItem) => void;
  requestType: 'Request Related' | 'Log Related';
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  promoRequestsFilter?: PromoRequestFilters;
  setPromoRequestsFilter?: (filter: PromoRequestFilters) => void;
  updateUrlState?: (filter: {
    userId?: string;
    expanded?: boolean;
    itemsExpanded?: boolean;
    logsExpanded?: boolean;
    failuresExpanded?: boolean;
    usersExpanded?: boolean;
  }) => void;
  setFormUserId?: (userId: string) => void;
  hidePromoCodeFields?: boolean;
}
const sharedAttrs: Pick<GridColDef, 'sortable' | 'display' | 'flex'> = {
  sortable: false,
  display: 'flex',
  flex: 1,
};

function PromoRequestItems({
  meta,
  promoRequestItems,
  isLoading,
  highlightedRows,
  onRowClick,
  requestType,
  expanded,
  onExpandedChange,
  promoRequestsFilter,
  setPromoRequestsFilter,
  updateUrlState,
  setFormUserId,
  hidePromoCodeFields = false,
}: PromoRequestItemsProps) {
  // Column visibility state for PromoRequestItems
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    itemId: false,
    categoryBK: false,
    subCategoryBK: false,
    travellers: false,
    isFnFBooking: true,
  });

  const handleColumnVisibilityChange = (field: string, visible: boolean) => {
    setColumnVisibilityModel((prev) => ({
      ...prev,
      [field]: visible,
    }));
  };

  const devColumns: Array<GridColDef<PromoRequestItem>> = [
    {
      field: 'categoryBK',
      headerName: 'Category',
      ...sharedAttrs,
      renderCell: (params) => (
        <PromoItemField field="categoryBK" categoryBK={params.row.categoryBK} value={params.row.categoryBK} />
      ),
    },
    {
      field: 'subCategoryBK',
      headerName: 'Sub Category',
      ...sharedAttrs,
      renderCell: (params) => (
        <PromoItemField field="subCategoryBK" categoryBK={params.row.categoryBK} value={params.row.subCategoryBK} />
      ),
    },
    {
      field: 'deviceType',
      headerName: 'Device Type',
      ...sharedAttrs,
      renderCell: (params) => (
        <>
          <PromoRequestSourceFormatter requestSource={params.row.requestSource as PromoRequestSource} />
          <PromoRequestDeviceType deviceType={params.row?.deviceType} requestSource={params.row?.requestSource} />
        </>
      ),
    },
    {
      field: 'travellers',
      headerName: 'Travellers',
      ...sharedAttrs,
      renderCell: (params) => {
        return params.row.travellers && <TravellersList withTitle={false} travellers={params.row.travellers} />;
      },
    },
    {
      field: 'itemId',
      headerName: 'Item ID',
      ...sharedAttrs,
      renderCell: (params) => (
        <PromoItemField field="itemId" categoryBK={params.row.categoryBK} value={params.row.itemId} />
      ),
    },
    {
      field: 'reservationType',
      headerName: 'Reservation Type',
      ...sharedAttrs,
      renderCell: (params) => (
        <PromoItemField field="reservationType" categoryBK={params.row.categoryBK} value={params.row.reservationType} />
      ),
    },
    {
      field: 'debug',
      headerName: '',
      ...sharedAttrs,
      renderCell: (params) => {
        return (
          <DebugModal
            type="generic"
            data={{
              ...params.row,
              fullRequest: params.row.fullRequest,
            }}
            title={`v${params.row?.fullRequest?.clientOrderVersion} ${params.row?.categoryBK} - ${params.row?.discountableTotal}`}
          />
        );
      },
    },
  ];

  const columns: Array<GridColDef<PromoRequestItem>> = [
    ...(hidePromoCodeFields
      ? []
      : [
          {
            field: 'codeName',
            headerName: 'Code Name',
            ...sharedAttrs,
            minWidth: 250,
            renderCell: (params) => <CopyableField value={params.row.codeName} label={params.row.codeName} />,
          },
        ]),
    {
      field: 'vouchersApplied',
      headerName: 'Vouchers Applied',
      ...sharedAttrs,
      minWidth: 200,
      flex: 2,
      renderCell: (params) => {
        const vouchers: Array<VoucherApplied> = params.row.vouchersApplied ?? [];
        if (vouchers.length === 0) {
          return null;
        }
        return (
          <Stack direction="column" spacing={0.5}>
            {vouchers.map((v, index) => {
              const currency = v.voucherCurrency || params.row.currencyCode;
              const amountText = v.amount == null ? 'missing' : currencyFormatter(currency, v.amount);
              const remainingText =
                v.remainingAmountAfter == null ? 'missing' : currencyFormatter(currency, v.remainingAmountAfter);
              return (
                <Box key={`${v.voucherId ?? 'v'}-${index}`}>
                  <Typography variant="body2" fontWeight="bold">
                    {v.voucherName || v.voucherTitle}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {amountText} (remaining: {remainingText})
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        );
      },
    },
    {
      field: 'requestAt',
      headerName: 'Request At',
      ...sharedAttrs,
      renderCell: (params) => (params.row.requestAt ? <DateFormatterDetailed date={params.row?.requestAt} /> : null),
    },
    {
      field: 'statusCode',
      headerName: 'Status Code',
      ...sharedAttrs,
      minWidth: 150,
      flex: 2,
      renderCell: (params) => (
        <>
          {params.row.discountItem?.warning}
          {statusCodeFormatter({ extended: true }, params.row.statusCode, params.row.errorCode, params.row.errorMsg)}
        </>
      ),
    },
    {
      field: 'userId',
      headerName: 'User',
      ...sharedAttrs,
      minWidth: 150,
      renderCell: (params) => {
        if (!params.row.userId) {
          return <Alert severity="warning">User ID not found</Alert>;
        }

        return (
          <PromoCodeUser
            userId={params.row.userId}
            userIdFor={params.row.userId}
            onUserIconClick={() => {
              if (updateUrlState) {
                updateUrlState({
                  userId: params.row.userId,
                  expanded: true,
                  itemsExpanded: true,
                  logsExpanded: true,
                  failuresExpanded: true,
                  usersExpanded: true,
                });
              }
              // Also update the local filter for immediate effect
              if (params.row.userId && promoRequestsFilter) {
                setPromoRequestsFilter?.({ ...promoRequestsFilter, userId: params.row.userId });
              }
              // Update the form field
              setFormUserId?.(params.row.userId ?? '');
            }}
          />
        );
      },
    },
    {
      field: 'productType',
      headerName: 'Product Type',
      ...sharedAttrs,
      minWidth: 200,
      renderCell: (params) => {
        if (!meta) {
          return (
            <Stack spacing={0.25}>
              <Typography>
                {params.row.categoryBK}, {params.row?.subCategoryBK}
              </Typography>
              <PromoItemField
                field="loyaltyOfferType"
                categoryBK={params.row.categoryBK}
                value={params.row.loyaltyOfferType}
              />
            </Stack>
          );
        }
        const title = getPromoProductDisplayName(
          meta.promo_product_types,
          params.row.categoryBK,
          params.row.subCategoryBK,
        );
        return (
          <Stack spacing={0.25}>
            <Typography title={title}>{title}</Typography>
            {params.row.loyaltyOfferType ? (
              <Typography variant="body2" color="text.secondary">
                {params.row.loyaltyOfferType}
              </Typography>
            ) : null}
          </Stack>
        );
      },
    },
    {
      field: 'transactionKey',
      headerName: 'Transaction Key',
      ...sharedAttrs,
      minWidth: 150,
      renderCell: (params) => {
        return (
          <PromoItemField
            field="transactionKey"
            categoryBK={params.row.categoryBK}
            title="Transaction Key"
            value={params.row.transactionKey}
            longDescription={`${params.row?.transactionKey} (Transaction Key)`}
          />
        );
      },
    },
    {
      field: 'pricing',
      headerName: 'Price',
      ...sharedAttrs,
      minWidth: 150,
      renderCell: (params) => {
        const isLuxPlusPrice = params.row.luxPlusPrice && params.row.luxPlusPrice > 0;
        return (
          <Typography component="div">
            <Stack>
              <Typography component="span" color={isLuxPlusPrice ? 'blue' : 'text.primary'}>
                {currencyFormatter(params.row.currencyCode, params.row.discountableTotal)}
              </Typography>
              {isLuxPlusPrice && (
                <Typography
                  component="span"
                  color="text.secondary"
                  title="Original Price"
                  sx={{ textDecoration: 'line-through' }}
                >
                  {currencyFormatter(params.row.currencyCode, params.row.luxPlusPrice ?? 0)}
                </Typography>
              )}
              <Typography component="div">
                <PromoItemField
                  field="mx"
                  categoryBK={params.row.categoryBK}
                  subCategoryBK={params.row.subCategoryBK}
                  value={params.row.mx ? '[MX]' : ''}
                />
              </Typography>
            </Stack>
          </Typography>
        );
      },
    },
    {
      field: 'voucherTotal',
      headerName: 'Voucher Total',
      ...sharedAttrs,
      minWidth: 120,
      renderCell: (params) => {
        if (params.row.voucherTotal == null) {
          return null;
        }
        return <Typography>{currencyFormatter(params.row.currencyCode, params.row.voucherTotal)}</Typography>;
      },
    },

    {
      field: 'fareType',
      headerName: 'Fare Type',
      ...sharedAttrs,
      minWidth: 150,
      renderCell: (params) => {
        return (
          <PromoItemField
            field="fareType"
            categoryBK={params.row.categoryBK}
            title="Fare Type"
            value={params.row.fareType}
            longDescription={`${params.row?.fareType} (Fare Type)`}
          />
        );
      },
    },
    {
      field: 'provider',
      headerName: 'Provider',
      ...sharedAttrs,
      minWidth: 150,
      renderCell: (params) => {
        return (
          <PromoItemField
            field="provider"
            categoryBK={params.row.categoryBK}
            value={params.row.provider}
            longDescription={`PROVIDER: ${params.row?.provider}`}
          />
        );
      },
    },
    {
      field: 'itemCountryCode',
      headerName: 'Country Code',
      ...sharedAttrs,
      renderCell: (params) => (
        <PromoItemField
          field="itemCountryCode"
          categoryBK={params.row.categoryBK}
          value={params.row.itemCountryCode}
          longDescription={`${params.row?.itemCountryCode} (Country Code)`}
        />
      ),
    },
    {
      field: 'itemLat',
      headerName: 'Lat/Lng',
      description: 'Item coordinates (decimal degrees) from the discount request, when present',
      ...sharedAttrs,
      minWidth: 140,
      renderCell: (params) => {
        const lat = params.row.itemLat;
        const lng = params.row.itemLng;
        if (lat == null && lng == null) {
          return (
            <Stack>
              <PromoItemField
                field="itemLat"
                categoryBK={params.row.categoryBK}
                value={params.row.itemLat ?? null}
                longDescription={`${params.row?.itemLat} (Latitude)`}
              />
              <PromoItemField
                field="itemLng"
                categoryBK={params.row.categoryBK}
                value={params.row.itemLng ?? null}
                longDescription={`${params.row?.itemLng} (Longitude)`}
              />
            </Stack>
          );
        }
        const latStr = lat != null ? String(lat) : '—';
        const lngStr = lng != null ? String(lng) : '—';
        return (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <CheckIcon fontSize="small" color="success" sx={{ flexShrink: 0 }} titleAccess="Has coordinates" />
            <CopyableField variant="body2" label={<></>} value={`${latStr}, ${lngStr}`} tooltip="Copy coordinates" />
          </Stack>
        );
      },
    },
    {
      field: 'isFnFBooking',
      headerName: 'FnF booking',
      description: 'Friends & family booking flag from the discount request item',
      ...sharedAttrs,
      minWidth: 110,
      renderCell: (params) => {
        const v = params.row.isFnFBooking;
        if (v === undefined || v === null) {
          return (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          );
        }
        return (
          <PromoItemField
            field="isFnFBooking"
            categoryBK={params.row.categoryBK}
            value={params.row.isFnFBooking ?? null}
            longDescription={`${params.row?.isFnFBooking} (Friends and family booking)`}
            displayOverride={
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  lineHeight: 0,
                  color: v ? 'success.main' : 'error.main',
                }}
                title={v ? 'Friends and family booking' : 'Not friends and family booking'}
                aria-label={v ? 'Friends and family booking' : 'Not friends and family booking'}
                role="img"
              >
                {v ? <CheckIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
              </Box>
            }
          />
        );
      },
    },
    {
      field: 'details',
      headerName: 'Details',
      ...sharedAttrs,
      renderCell: (params) => {
        return (
          <Stack direction="column" spacing={0}>
            <PromoItemField
              field="numberOfNights"
              categoryBK={params.row.categoryBK}
              title="N"
              value={params.row.numberOfNights}
              longDescription={`${params.row?.numberOfNights} nights (Number of Nights)`}
            />
            <PromoItemField
              field="numberOfAdults"
              categoryBK={params.row.categoryBK}
              title="A"
              value={params.row.numberOfAdults}
              longDescription={`${params.row?.numberOfAdults} adults (Number of Adults`}
            />
            <PromoItemField
              field="numberOfChildren"
              categoryBK={params.row.categoryBK}
              title="C"
              value={params.row.numberOfChildren}
              longDescription={`${params.row.numberOfChildren} children (Number of Children)`}
            />
            <PromoItemField
              field="originAirportCode"
              categoryBK={params.row.categoryBK}
              title="Origin Airport"
              value={params.row.originAirportCode}
              longDescription={`${params.row.originAirportCode} (Origin Airport) aka Leaving from airport`}
            />
            <PromoItemField
              field="destinationAirportCode"
              categoryBK={params.row.categoryBK}
              title="Destination Airport"
              value={params.row.destinationAirportCode}
              longDescription={`${params.row.destinationAirportCode} (Destination Airport) aka Going to airport`}
            />
            {params.row.discountItem?.matchingDiscount && (
              <DebugModal
                data={params.row.discountItem.matchingDiscount}
                title={`Matching Discount ${params.row.discountItem.discountAmount}`}
                type="generic"
              />
            )}
          </Stack>
        );
      },
    },
    ...devColumns,
  ];

  if (isLoading) {
    return <Spinner size={36} />;
  }

  return (
    <Accordion expanded={expanded} onChange={(_, expanded) => onExpandedChange?.(expanded)}>
      <AccordionSummary
        expandIcon={<GridExpandMoreIcon />}
        aria-controls="offer-fields"
        id="offer-fields"
        sx={{
          backgroundColor: theme.palette.grey[200],
        }}
      >
        <PageSubheader title={`Promo ${requestType} Items (${promoRequestItems?.length ?? 0})`} />
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ position: 'relative' }}>
          <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
            <ColumnSelector
              columns={columns}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityChange={handleColumnVisibilityChange}
            />
          </Box>

          <Table
            columns={columns}
            rows={promoRequestItems ?? []}
            paginationMode="client"
            getRowId={(row: PromoRequestItem) => `${row?.uniqueKey}`}
            dataGridProps={{
              getRowHeight: () => 'auto',
              getRowClassName: (params) => {
                const requestKey = getRequestKey(params.row.requestAt, params.row.codeName);
                return highlightedRows.includes(requestKey) ? 'highlighted-row' : '';
              },
              columnVisibilityModel,
              onColumnVisibilityModelChange: setColumnVisibilityModel,
              sx: {
                '& .highlighted-row': {
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.12)',
                  },
                },
              },
            }}
          />
        </Box>
        <Typography variant="subtitle1">
          (This is a display of the items in the "Recent Request" table above.)
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
}

export default PromoRequestItems;
